import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const domainData = [
  { name: 'Reading', before: 18, after: 23 },
  { name: 'Listening', before: 16, after: 22 },
  { name: 'Speaking', before: 14, after: 20 },
  { name: 'Writing', before: 14, after: 20 },
];

const SocialProof: React.FC = () => {
  return (
    <div className="py-24 bg-[#FAF7F2]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <h2 className="text-pink-300 font-bold tracking-widest text-sm uppercase mb-4">Real Results</h2>
          <h3 className="text-3xl md:text-4xl font-extrabold text-slate-800">먼저 경험한 학습자들의 놀라운 변화</h3>
        </div>

        {/* Detailed Case Study Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start mb-24">
          <div className="lg:col-span-7 bg-white p-8 md:p-10 rounded-[3rem] shadow-xl border border-[#F2ECE4]">
             <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                <div>
                  <h4 className="text-xl font-bold flex items-center text-slate-700">
                    <span className="w-1.5 h-6 bg-pink-300 rounded-full mr-3"></span>
                    4대 영역 성적 변화
                  </h4>
                  <p className="text-slate-400 text-sm mt-1 font-medium">30회 밀착 코칭 및 AI 첨삭 집중 훈련 결과</p>
                </div>
                <div className="flex items-center bg-pink-50 px-4 py-2 rounded-2xl border border-pink-100/50">
                   <div className="text-center mr-4 pr-4 border-r border-pink-100">
                      <div className="text-[10px] font-bold text-slate-400 uppercase">Total Before</div>
                      <div className="text-xl font-black text-slate-500">62</div>
                   </div>
                   <div className="text-center">
                      <div className="text-[10px] font-bold text-pink-500 uppercase">Total After</div>
                      <div className="text-xl font-black text-pink-500">85</div>
                   </div>
                </div>
             </div>

             <div className="h-[350px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={domainData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F2ECE4" />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{fill: '#94a3b8', fontWeight: 600, fontSize: 13}} 
                    />
                    <YAxis domain={[0, 30]} axisLine={false} tickLine={false} tick={{fill: '#cbd5e1', fontSize: 11}} />
                    <Tooltip 
                      cursor={{fill: '#fdf2f8', opacity: 0.4}} 
                      contentStyle={{borderRadius: '20px', border: 'none', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', padding: '15px'}}
                    />
                    <Legend 
                      verticalAlign="top" 
                      align="right" 
                      iconType="circle"
                      formatter={(value) => {
                        const color = value === '코칭 전' ? '#94A3B8' : '#F43F5E';
                        return <span style={{ color, fontWeight: '900', fontSize: '14px' }}>{value}</span>;
                      }}
                      wrapperStyle={{ paddingBottom: '20px' }}
                    />
                    {/* 코칭 전: 연한 그레이, 코칭 후: 강렬한 로즈 핑크로 시인성 증대 */}
                    <Bar name="코칭 전" dataKey="before" fill="#CBD5E1" radius={[6, 6, 0, 0]} barSize={25} />
                    <Bar name="30회 코칭 후" dataKey="after" fill="#F43F5E" radius={[6, 6, 0, 0]} barSize={25} />
                  </BarChart>
                </ResponsiveContainer>
             </div>
             
             <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                   <h5 className="text-xs font-bold text-slate-500 uppercase mb-2">Speaking & Writing Focus</h5>
                   <p className="text-sm text-slate-600 leading-snug">
                     가장 취약했던 출력 영역에서 <b>AI 실시간 첨삭</b>을 통해 문법 오류를 90% 감소시키고 아카데믹 어휘 사용 빈도를 3배 높였습니다.
                   </p>
                </div>
                <div className="p-4 bg-pink-50/50 rounded-2xl border border-pink-100/50">
                   <h5 className="text-xs font-bold text-pink-400 uppercase mb-2">Reading & Listening Focus</h5>
                   <p className="text-sm text-slate-600 leading-snug">
                     TOEFL 공식 기출 자료의 <b>시그널 워드 분석 훈련</b>으로 긴 지문에서의 핵심 파악 속도를 40% 단축시켰습니다.
                   </p>
                </div>
             </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-[#F2ECE4] relative overflow-hidden group">
               <div className="absolute top-0 right-0 w-24 h-24 bg-pink-50 rounded-full -mr-8 -mt-8 transition-transform group-hover:scale-110"></div>
               <div className="relative z-10">
                  <div className="flex items-center space-x-4 mb-5">
                    <img src="https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=64" alt="User" className="w-14 h-14 rounded-full object-cover border-2 border-pink-100" />
                    <div>
                      <h5 className="font-bold text-slate-800 text-lg">교환학생 준비생</h5>
                      <div className="flex text-pink-300 text-xs tracking-widest mt-0.5">★★★★★</div>
                    </div>
                  </div>
                  <p className="text-slate-500 italic text-sm leading-relaxed mb-4">
                    "혼자 할 때는 60점대에서 멈췄는데, AI 코치님이 제 발음과 문장 구조를 직접 잡아주시니 20회차 만에 85점을 넘겼어요! 특히 스피킹 독립형에서 당황하지 않고 말하는 템플릿 체득이 큰 도움이 됐습니다."
                  </p>
                  <div className="inline-block px-3 py-1 bg-pink-50 text-pink-500 text-[10px] font-black rounded-full">20회차 만에 목표 달성</div>
               </div>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-[#F2ECE4] relative overflow-hidden group">
               <div className="absolute top-0 right-0 w-24 h-24 bg-slate-50 rounded-full -mr-8 -mt-8 transition-transform group-hover:scale-110"></div>
               <div className="relative z-10">
                  <div className="flex items-center space-x-4 mb-5">
                    <img src="https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&q=80&w=64" alt="User" className="w-14 h-14 rounded-full object-cover border-2 border-slate-100" />
                    <div>
                      <h5 className="font-bold text-slate-800 text-lg">직장인</h5>
                      <div className="flex text-pink-300 text-xs tracking-widest mt-0.5">★★★★★</div>
                    </div>
                  </div>
                  <p className="text-slate-500 italic text-sm leading-relaxed mb-4">
                    "토플 자료로 공부하니 단순 회화를 넘어 비즈니스 리포트 작성까지 자신감이 생겼습니다. 퇴근 후 밤늦게 에세이를 써도 AI가 즉시 피드백을 주니 학습 흐름이 끊기지 않아 효율적이었어요."
                  </p>
                  <div className="inline-block px-3 py-1 bg-slate-50 text-slate-500 text-[10px] font-black rounded-full">퇴근 후 1시간 밀착 케어</div>
               </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
           {[
             { val: '95%', label: '목표 점수 달성률' },
             { val: '1,000+', label: '누적 코칭 횟수' },
             { val: '24/7', label: '실시간 AI 피드백' },
             { val: 'Official', label: 'TOEFL 파트너' }
           ].map((stat, i) => (
             <div key={i} className="text-center p-6 rounded-3xl hover:bg-white hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl md:text-4xl font-black text-pink-300 mb-2 group-hover:text-pink-400 transition-colors">{stat.val}</div>
                <div className="text-slate-400 font-bold text-xs uppercase tracking-wider">{stat.label}</div>
             </div>
           ))}
        </div>
      </div>
    </div>
  );
};

export default SocialProof;